/**
 * The archive teaser — "83 memories archived for Sarah" (PPL-377, on PPL-350's
 * frames).
 *
 * ## The fence that walks toward the user
 *
 * Every other limit here is static: five ppl, thirty days. This number grows on
 * its own, because something meaningful ages past the window every day a free
 * user keeps talking to f8. That is what makes it a teaser rather than a lock —
 * nothing is withheld from a screen, something quietly accumulates on the other
 * side of a line.
 *
 * ## What it must never say
 *
 * Archived, stored, preserved. Never lost, deleted or gone. There is no lock
 * icon, no greyed row list, no "upgrade to see" scroll — dormant memories are
 * simply not there to browse; they are counted.
 *
 * ## It renders NOTHING in three cases
 *
 *   * no window in force — premium, or the fence still off server-side. While it
 *     is off those memories are not dormant, so the row's central claim is false.
 *   * a count of zero — nothing is on the other side of the line yet.
 *   * a count of `null` — not established. "0 memories archived" shown to someone
 *     with eighty is worse than no row.
 */

import React from 'react';
import { ArchiveGlyph } from './glyphs';
import { RT } from './tokens';

interface Props {
  archived: number | null;
  memoryWindowDays: number | null;
  /**
   * Whether f8 has paused this person — stopped adding to their story.
   *
   * It changes what the number MEANS, so it changes the sentence. For an active
   * person `archived` is what aged past the window. For a paused one the server
   * returns their WHOLE record regardless of age — so "older than 30 days" would
   * be flatly false for someone paused yesterday with only last week's entries.
   */
  paused?: boolean;
  /** PERSONAL register. */
  firstName: string;
  onRestore?: () => void;
}

export default function ArchiveRow({
  archived, memoryWindowDays, paused = false, firstName, onRestore,
}: Props) {
  if (memoryWindowDays === null || memoryWindowDays <= 0) return null;
  if (archived === null || archived <= 0) return null;

  const who = firstName || 'them';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '11px 15px 13px', marginTop: 10,
      borderTop: `1px dashed ${RT.borderStrong}`,
    }}>
      <ArchiveGlyph size={15} color={RT.dimSolid} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: RT.muted }}>
          {archived} {archived === 1 ? 'memory' : 'memories'} archived for {who}
        </span>
        <span style={{ display: 'block', fontSize: 10.5, color: RT.dim, marginTop: 1 }}>
          {paused
            ? 'stored while f8 is paused \u2014 nothing was deleted'
            : `older than ${memoryWindowDays} days \u2014 stored, invisible to f8 on free`}
        </span>
      </span>
      {onRestore ? (
        <button
          type="button"
          onClick={onRestore}
          style={{
            appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
            padding: '6px 12px', borderRadius: 999,
            background: RT.surface, border: `1px solid ${RT.border}`,
            color: RT.muted, fontSize: 11.5, fontWeight: 600,
          }}
        >
          restore
        </button>
      ) : null}
    </div>
  );
}
